import * as Dialog from "@radix-ui/react-dialog"
import { clsx } from "@/src/utils/clsx"

export default function Modal({
    open,
    onOpenChange,
    children
}: {
    open?: boolean
    onOpenChange?: (open: boolean) => void
    children: React.ReactNode
}) {
    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            {children}
        </Dialog.Root>
    )
}

const MODAL_CONTENT_TRANSITION_SETTINGS = {
    fadeInOut:
        "transition-opacity duration-150 data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in ease-custom-ease lg:duration-300",
    slideInOutRight:
        "transition-all duration-300 delay-100 data-[state=closed]:animate-slide-out-right data-[state=open]:animate-slide-in-right ease-custom-ease"
}

const ModalContent = ({
    children,
    className,
    transitionSettings = "fadeInOut",
    dismissOnOutsideClick = true,
    onCloseFocus
}: {
    children: React.ReactNode
    className?: string
    transitionSettings?: keyof typeof MODAL_CONTENT_TRANSITION_SETTINGS
    /**
     * Set false when the modal asks a question that needs an answer. The close button and
     * Escape still work.
     */
    dismissOnOutsideClick?: boolean
    /**
     * Replaces the default of returning focus to whatever opened the modal. Runs once the
     * focus trap is down, which a button's own click handler cannot wait for.
     */
    onCloseFocus?: () => void
}) => {
    return (
        <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-[var(--z-critical-modal-backdrop)] bg-black/10 backdrop-blur-md duration-150 ease-custom-ease data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in lg:duration-200" />
            {/* No `title` here: Radix names the dialog from its `Modal.Title`, and a static
                one would announce every modal as the same thing. */}
            <Dialog.Content
                onInteractOutside={
                    dismissOnOutsideClick ? undefined : event => event.preventDefault()
                }
                onCloseAutoFocus={
                    onCloseFocus &&
                    (event => {
                        event.preventDefault()
                        onCloseFocus()
                    })
                }
                className={clsx(
                    "fixed bottom-0 right-0 z-[var(--z-critical-modal)] h-auto w-full bg-white px-12 pt-12 lg:m-24 lg:w-[448px] lg:p-16",
                    className,
                    transitionSettings &&
                        MODAL_CONTENT_TRANSITION_SETTINGS[transitionSettings]
                )}
            >
                {children}
            </Dialog.Content>
        </Dialog.Portal>
    )
}

Modal.Button = Dialog.Trigger
Modal.Content = ModalContent
Modal.Close = Dialog.Close
Modal.Title = Dialog.Title
Modal.Description = Dialog.Description
